/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeType } from 'react-tailwindcss-datepicker';
import { Button } from '@mui/material';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { Code } from 'models/common/CommonCode';
import { PageMode } from 'models/common/Common';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { findProject, getSetupProjectList } from 'apis/setup/SetupProject';
import { findDailyReport } from 'apis/setup/SetupDailyReport';
import { SetupProject } from 'models/setup/SetupProject';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { CommonUtil } from 'utils/CommonUtil';
import { ManagementMode } from 'models/common/Common';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'components/process/Loading';
import { SetupDailyReport, SetupDailyReportCondition } from 'models/setup/SetupDailyReport';
import useSessionStore from 'stores/useSessionStore';
import { EVENT_TYPES, EventBus } from '../../utils/EventUtil';

/**
 * @desc 설비셋업관리 > 프로젝트 셋업관리 > 프로젝트 셋업 일보 조회 화면
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jd            신규 생성
 * 2026.02.24   jihwan89      작업일보 페이지 이동 시 파라미터 추가 / 날짜, 일보리스트
 */
const SetupDailyReportPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();
  const gridRef = useRef<any>();
  const { userId, deptCd, deptNm, ensolYn, elmPrjId } = useSessionStore();

  const defaultCondition = {
    useYn: '',
    crtnStDt: '',
    crtnEndDt: '',
    excpId: ensolYn === 'Y' ? '' : deptCd,
  };

  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]);
  const [condition, setCondition] = useState<SetupDailyReportCondition>(defaultCondition);
  const [errors, setErrors] = useState<any>({});
  const [code, setCode] = useState<any>({});
  const [rowData, setRowData] = useState<any[]>([]);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (condition?.bildPlntCds || []).length > 0
          ? condition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, condition?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  useEffect(() => {
    findProject({ searchScheduleYn: 'Y', searchScheduleRegYn: 'Y' }).then(
      (result: SetupProject[]) => {
        // 조회조건 자동설정
        if (elmPrjId) {
          handleOnChange('elmPrjId', elmPrjId);
        } else if ((result || []).length && result[0]?.elmPrjId) {
          handleOnChange('elmPrjId', result[0].elmPrjId);
        }
        setPrjMasterData(result);
      }
    );
    getCommonCodes();
  }, []);

  useEffect(() => {
    const unsubscribe = EventBus.subscribe(EVENT_TYPES.SETUP_DAILY_LIST, () => {
      handleSearch(condition);
    });
    return unsubscribe;
  }, [condition?.elmPrjId, condition?.bildPlntCds, condition?.excpId]);

  useEffect(() => {
    if (condition?.elmPrjId) {
      handleSearch(condition);
      // 협력사인 경우 고정
      const excpId = ensolYn === 'Y' ? '' : deptCd;
      findExcpByElmPrjId(condition?.elmPrjId, excpId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
  }, [condition?.elmPrjId, condition?.excpId, ensolYn]);

  const getCommonCodes = async () => {
    const bildPlntCd: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD'); // 플랜트코드

    setCode({
      bildPlntCd: bildPlntCd,
    });
  };

  const handleOnChange = (name, value) => {
    setCondition((prev) => ({
      ...prev,
      [name]: name === 'excpId' && ensolYn !== 'Y' ? deptCd : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const validate = () => {
    const err = {};
    // 프로젝트명
    if (!condition?.elmPrjId) {
      Object.assign(err, { elmPrjId: true });
    }
    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  const moveToPage = (item: any) => {
    if (item?.elmPrjId) {
      if (item?.wkStatCd === 'SAVE' && item?.dataInsUserId !== userId) {
        openMessageBar({
          type: 'warning',
          content:
            `${item?.dataInsUserNm}` +
            String(t('com.msg.님이 작성중입니다.', '님이 작성중입니다.')),
        });
        return;
      }

      const searchPageCondition = {
        mode: PageMode.EDIT,
        upprMnuUrl: location.pathname,
        mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
        mnuNm: t('setup.label.셋업일보 상세 조회', '셋업일보 상세 조회'),
        elmPrjId: item.elmPrjId,
        excpId: item.excpId,
        excpNm: item.excpNm,
        reportList: rowData,
        crtnDt: item.crtnDt,
      };
      navigate(
        `/setup/daily-report/detail?elmPrjId=${item.elmPrjId}&excpId=${item.excpId}&crtnDt=${
          item.crtnDt ?? ''
        }`,
        {
          state: { ...searchPageCondition },
        }
      );
    }
  };

  const handleSearch = (condition) => {
    if (!validate()) {
      return;
    }

    openLoading(true);

    findDailyReport({ ...(condition || {}), dailyReportYn: 'Y' })
      .then((result: SetupProject[]) => {
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 일보 목록', '셋업 일보 목록'),
      t('setup.label.셋업 일보 목록', '셋업 일보 목록')
    );
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const layoutDefinition = [
    {
      binding: 'bildPlntCdNm',
      header: String(t('setup.label.플랜트', '플랜트')),
      width: 250,
      align: 'center',
    },
    {
      binding: 'elmPrjNm',
      header: String(t('setup.label.프로젝트명', '프로젝트명')),
      minWidth: 130,
      width: '*',
      align: 'left',
      cellTemplate: CellMaker.makeLink({
        click: (e, ctx) => {
          moveToPage(ctx.item);
        },
      }),
    },
    {
      binding: 'wkStatCdNm',
      header: String(t('setup.label.상태', '상태')),
      width: 130,
      align: 'center',
    },
    {
      binding: 'excpNm',
      header: String(t('setup.label.협력업체명', '협력업체명')),
      width: 190,
      align: 'center',
    },
    {
      binding: 'dataInsUserNm',
      header: String(t('setup.label.작성자', '작성자')),
      width: 180,
      align: 'center',
    },
    {
      binding: 'crtnDt',
      header: String(t('setup.label.작업일', '작업일')),
      width: 165,
      align: 'center',
    },
    {
      binding: 'dataInsDtm',
      header: String(t('setup.label.작성일', '작성일')),
      width: 165,
      align: 'center',
    },
  ];

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('setup.label.플랜트', '플랜트'))}</span>
                </label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.bildPlntCd || []}
                  defaultValue={condition.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          condition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                      handleOnChange('excpId', '');
                    }
                  }}
                  className="width25"
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span className="dot">{String(t('setup.label.프로젝트명', '프로젝트명'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    handleOnChange('excpId', '');
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span className="dot">{String(t('setup.label.협력업체명', '협력업체명'))}</span>
                </label>
                {ensolYn === 'Y' ? (
                  <ComboBox
                    placeholder={String(t('com.label.선택', '선택'))}
                    options={optionsPrjExcp}
                    defaultValue={condition?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{deptNm}</span>
                  </div>
                )}
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('setup.label.작업일', '작업일'))}</span>
                </label>
                <CustomDatepicker
                  minDate={null}
                  value={{
                    startDate: condition?.crtnStDt || '',
                    endDate: condition?.crtnEndDt || '',
                  }}
                  onChange={(newValue) => {
                    const value = newValue as DateRangeType;
                    setCondition((prev) => ({
                      ...prev,
                      crtnStDt: (value?.startDate as string) || '',
                      crtnEndDt: (value?.endDate as string) || '',
                    }));
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 일보 목록', '셋업 일보 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>

      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={580}
        isSelector={false}
        isFilter={true}
        isReadOnly={true}
        allowPinning={false}
        initialized={onInitialized}
      />

      {ensolYn === 'N' && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="icoNone primaryBtn"
            onClick={() => {
              if (!condition?.elmPrjId) {
                openMessageBar({
                  type: 'warning',
                  content: t(
                    'setup.label.대상 프로젝트가 조회 되지 않았습니다.',
                    '프로젝트가 조회 되지 않았습니다.'
                  ),
                });
                return;
              }
              if (!condition?.excpId) {
                openMessageBar({
                  type: 'warning',
                  content: t('setup.label.협력업체를 선택해 주세요.', '협력업체를 선택해 주세요.'),
                });
                return;
              }
              if (!prjExcpData) {
                openMessageBar({
                  type: 'warning',
                  content: t('setup.label.협력업체 정보가 없습니다.', '협력업체 정보가 없습니다.'),
                });
                return;
              }
              navigate(`/setup/daily-report/new`, {
                state: {
                  upprMnuUrl: location.pathname,
                  mode: PageMode.WRITE,
                  mnuId: `${location.pathname}/${ManagementMode.CREATE}`,
                  mnuNm: t('setup.label.셋업일보 등록', '셋업일보 등록'),
                  elmPrjId: condition.elmPrjId,
                  excpId: condition.excpId,
                  excpNm: condition.excpNm,
                },
              });
            }}
          >
            {t('setup.label.작업일보 등록', '작업일보 등록')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default SetupDailyReportPage;
